import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { CaretDown, Loading } from '~/ui/assets/icons';
import { CascaderProps } from 'antd/lib/cascader';
import { createPortal } from 'react-dom';
import includesStringSearch from '~/common/includesStringSearch';
import { CustomComponentsAreaProps, PortalSelect, SelectLabel, StyledCascader } from './Select.styled';

type SelectProps = Omit<CascaderProps, 'options'> & {
  options: any[];
  label?: string;
  loading?: boolean;
  backgroundColor?: string;
  popupPlacement?: 'left' | 'right';
  testId?: string;
  resize?: boolean;
};

const Portal = (props: CustomComponentsAreaProps) =>
  createPortal(<PortalSelect id="mt-select-portal" {...props} />, document.getElementById('portal'));

export const Select = forwardRef(
  ({ label, loading, popupPlacement = 'right', placeholder, testId = 'cascader', ...props }: SelectProps) => {
    const [dropdownWidth, setDropdownWidth] = useState<number | null>(0);
    const selectRef = useRef(null);
    const theme = useTheme();

    function filter(inputValue, path) {
      const labelField = props.fieldNames?.label || 'label';
      return path.some((option) => includesStringSearch(option[labelField], inputValue));
    }

    const getPopupContainer = () => document.getElementById('mt-select-portal');

    useEffect(() => {
      if (selectRef?.current) {
        setTimeout(() => {
          const inputWidth = selectRef?.current?.input?.input?.parentElement?.offsetWidth;
          setDropdownWidth(inputWidth);
        }, 100);
      }
    }, [selectRef, props.resize]);

    return (
      <div>
        <SelectLabel data-testid={`${testId}-select-label`}>{label || placeholder}</SelectLabel>
        <StyledCascader
          ref={selectRef}
          notFoundContent="Nenhum resultado encontrado"
          size="large"
          suffixIcon={loading ? <Loading fill={theme.colors.element.primary} /> : <CaretDown />}
          showSearch={{ filter }}
          allowClear
          disabled={loading}
          {...props}
          label={null}
          placeholder={null}
          getPopupContainer={getPopupContainer}
          popupStyle={dropdownWidth ? { minWidth: dropdownWidth } : {}}
          popupClassName={['mt-select-dropdown', props.popupClassName, popupPlacement].filter(Boolean).join(' ')}
          data-testid={`${testId}-select`}
        />

        <Portal newWidth={dropdownWidth} />
      </div>
    );
  },
);
